import { supabase } from '../lib/supabase'

export interface ServiceItemChecklistTask {
  label: string
  sort_order: number
  photo_required?: boolean
}

export interface ServiceItem {
  id: string
  service_category_id: string
  name: string
  description?: string
  work_description?: string | null
  default_checklist?: ServiceItemChecklistTask[]
  unit_type: string
  base_price: number
  is_active: boolean
  is_seasonal?: boolean
  season_start_month?: number | null
  season_end_month?: number | null
  category?: {
    id: string
    name: string
  }
}

export interface ServiceItemGrouped {
  category: {
    id: string
    name: string
  }
  items: ServiceItem[]
}

const SERVICE_ITEM_COLUMNS = `
  id,
  service_category_id,
  name,
  description,
  work_description,
  default_checklist,
  unit_type,
  base_price,
  is_active,
  is_seasonal,
  season_start_month,
  season_end_month
`

function mapServiceItemFields(item: any, categoryInfo?: { id: string; name: string }): ServiceItem {
  return {
    id: item.id,
    service_category_id: item.service_category_id || '',
    name: item.name,
    description: item.description,
    work_description: item.work_description ?? null,
    default_checklist: Array.isArray(item.default_checklist) ? item.default_checklist : [],
    unit_type: item.unit_type,
    base_price: parseFloat(item.base_price) || 0,
    is_active: item.is_active,
    is_seasonal: !!item.is_seasonal,
    season_start_month: item.season_start_month ?? null,
    season_end_month: item.season_end_month ?? null,
    category: categoryInfo,
  }
}

function parseCategoryInfo(categories: any): { id: string; name: string } | null {
  if (!categories) return null
  if (Array.isArray(categories) && categories.length > 0) {
    return { id: categories[0].id, name: categories[0].name }
  }
  if (typeof categories === 'object' && categories.id) {
    return { id: categories.id, name: categories.name }
  }
  return null
}

/**
 * Fetch all active service items grouped by category
 * @returns Array of service items grouped by category, sorted by category name, then item name
 */
export async function fetchServiceItemsGrouped(): Promise<ServiceItemGrouped[]> {
  try {
    // First, try to fetch without join to see if table exists and has data
    const { data: testData, error: testError } = await supabase
      .from('service_items')
      .select('id, name, is_active')
      .limit(1)

    if (testError) {
      console.error('Error accessing service_items table:', testError)
      // Table might not exist - return empty array
      return []
    }

    console.log('Service items table accessible. Test data:', testData)

    // Now fetch with join - explicitly specify the foreign key
    const { data, error } = await supabase
      .from('service_items')
      .select(
        `
        ${SERVICE_ITEM_COLUMNS},
        categories:service_categories!service_category_id (
          id,
          name
        )
      `
      )
      .eq('is_active', true)
      .order('name')

    if (error) {
      console.error('Error fetching service items with join:', error)
      // Try without join as fallback
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('service_items')
        .select(SERVICE_ITEM_COLUMNS)
        .eq('is_active', true)
        .order('name')

      if (fallbackError) {
        console.error('Error fetching service items (fallback):', fallbackError)
        return []
      }

      if (!fallbackData || fallbackData.length === 0) {
        console.warn('No active service items found in database')
        return []
      }

      console.log('Using fallback query (without join). Items found:', fallbackData.length)
      // Process fallback data without category info
      const fallbackGrouped: ServiceItemGrouped[] = []
      const uncategorizedItems: ServiceItem[] = []

      fallbackData.forEach((item: any) => {
        uncategorizedItems.push(mapServiceItemFields(item))
      })

      if (uncategorizedItems.length > 0) {
        fallbackGrouped.push({
          category: {
            id: 'uncategorized',
            name: 'Uncategorized',
          },
          items: uncategorizedItems,
        })
      }

      return fallbackGrouped
    }

    if (!data) {
      console.warn('No service items data returned from query')
      return []
    }

    console.log('Raw service items data:', data)
    console.log('Number of items:', data.length)

    // Group by category
    const categoryMap = new Map<string, ServiceItemGrouped>()

    data.forEach((item: any) => {
      const categoryInfo = parseCategoryInfo(item.categories)
      const categoryId = item.service_category_id || categoryInfo?.id || 'uncategorized'
      const categoryName = categoryInfo?.name || 'Uncategorized'

      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, {
          category: {
            id: categoryId,
            name: categoryName,
          },
          items: [],
        })
      }

      categoryMap.get(categoryId)!.items.push(mapServiceItemFields(item, categoryInfo || undefined))
    })

    // Convert map to array and sort
    const grouped = Array.from(categoryMap.values())
    grouped.sort((a, b) => a.category.name.localeCompare(b.category.name))

    // Sort items within each category
    grouped.forEach((group) => {
      group.items.sort((a, b) => a.name.localeCompare(b.name))
    })

    return grouped
  } catch (error) {
    console.error('Error fetching service items:', error)
    return []
  }
}

/**
 * Fetch a single service item by ID
 */
export async function fetchServiceItemById(id: string): Promise<ServiceItem | null> {
  try {
    const { data, error } = await supabase
      .from('service_items')
      .select(
        `
        ${SERVICE_ITEM_COLUMNS},
        categories:service_categories!service_category_id (
          id,
          name
        )
      `
      )
      .eq('id', id)
      .eq('is_active', true)
      .single()

    if (error) throw error
    if (!data) return null

    return mapServiceItemFields(data, parseCategoryInfo(data.categories) || undefined)
  } catch (error) {
    console.error('Error fetching service item:', error)
    return null
  }
}
